/**
 * SpotModeMarker
 * 
 * @atomic - Handles spot mode draggable marker rendering
 * @idempotent - Pure component with React.memo
 * @modular - Self-contained marker with drag handling
 * 
 * Purpose: Renders draggable marker for spot mode location selection
 * Props: Location, handlers, user location for distance calculation
 * Returns: Draggable Leaflet marker with validation and analytics
 */

import { memo } from 'react'
import { Marker, Popup } from 'react-leaflet'
import { useTranslation } from 'react-i18next'
import { MapPinIcon } from '@heroicons/react/24/outline'
import { toast } from '../../services/toastService'
import { spotModeIcon } from '../../config/mapIcons'
import { isValidLocation } from '../../utils/mapValidation'
import { trackSpotModeLocationSet } from '../../utils/analytics'
import { createLogger } from '../../utils/logger'

const logger = createLogger('SpotModeMarker')

const SpotModeMarker = memo(function SpotModeMarker({
  location,
  onLocationChange,
  userLocation = null
}) {
  const { t } = useTranslation()

  if (!location) return null

  const handleDragEnd = (e) => {
    const marker = e.target
    const position = marker.getLatLng()
    
    // Validate coordinates before setting
    if (!isValidLocation({ lat: position.lat, lng: position.lng })) {
      logger.error('Invalid coordinates from marker drag', { position })
      toast.error(t('toast.error.invalidLocation'))
      return
    }
    
    const newLocation = { lat: position.lat, lng: position.lng }
    
    // Track location adjustment via marker drag
    let distanceFromUser = null
    if (userLocation) {
      const R = 6371 // Earth's radius in km
      const dLat = (newLocation.lat - userLocation.latitude) * Math.PI / 180
      const dLon = (newLocation.lng - userLocation.longitude) * Math.PI / 180
      const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(userLocation.latitude * Math.PI / 180) * Math.cos(newLocation.lat * Math.PI / 180) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2)
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
      distanceFromUser = R * c
    }
    
    trackSpotModeLocationSet('marker_drag', false, distanceFromUser, null)
    
    onLocationChange(newLocation)
  }

  return (
    <Marker
      key="spot-marker"
      position={[location.lat, location.lng]}
      icon={spotModeIcon}
      draggable={true}
      eventHandlers={{
        dragend: handleDragEnd
      }}
    >
      <Popup>
        <div className="text-sm">
          <p className="font-semibold mb-1 flex items-center gap-1">
            <MapPinIcon className="h-4 w-4" />
            {t('map.sightingLocation')}
          </p>
          <p className="text-xs text-gray-600">{t('map.spotMode.dragMarker')}</p>
        </div>
      </Popup>
    </Marker>
  )
})

export default SpotModeMarker
